import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { categories } from '@/mocks/categories';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  if (!slug) {
    return <Navigate to="/" replace />;
  }

  const article = getArticleBySlug(slug);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const cat = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = getRelatedArticles(article);

  const articleJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.imageUrl,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      datePublished: article.date,
      publisher: {
        '@type': 'Organization',
        name: '亲密发声训练指南',
        url: 'https://auxnuyb.cn',
      },
      url: `https://auxnuyb.cn/article/${article.slug}`,
      about: article.tags.map((tag: string) => ({ '@type': 'Thing', name: tag })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://auxnuyb.cn',
        },
        ...(cat
          ? [
              {
                '@type': 'ListItem',
                position: 2,
                name: cat.name,
                item: `https://auxnuyb.cn/category/${article.categoryId}`,
              },
            ]
          : []),
        {
          '@type': 'ListItem',
          position: cat ? 3 : 2,
          name: article.title,
          item: `https://auxnuyb.cn/article/${article.slug}`,
        },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalPath={`/article/${article.slug}`}
        ogType="article"
        ogImage={article.imageUrl}
        jsonLd={articleJsonLd}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <article>
          {/* Article Header */}
          <header className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <Breadcrumb />
              <div className="py-8 md:py-10">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {cat && (
                    <Link
                      to={`/category/${cat.id}`}
                      className="px-3 py-1 text-xs rounded-full bg-primary-100 text-primary-700 font-medium hover:bg-primary-200 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </Link>
                  )}
                  <span className="text-xs text-foreground-500 whitespace-nowrap">{article.date}</span>
                  <span className="text-xs text-foreground-400">·</span>
                  <span className="text-xs text-foreground-500 whitespace-nowrap">{article.readTime}</span>
                  <span className="text-xs text-foreground-400">·</span>
                  <span className="text-xs text-foreground-500 whitespace-nowrap">{article.author}</span>
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading leading-tight mb-4">
                  {article.title}
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {article.excerpt}
                </p>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="max-w-4xl mx-auto px-4 md:px-6 pt-8 md:pt-10">
            <div className="w-full h-56 md:h-80 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 亲密发声训练指南`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Article Content */}
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div
              className="prose-custom"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 mr-2 whitespace-nowrap">标签：</span>
                {article.tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100 border-t border-background-200/70">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6 md:mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((rel) => {
                  const relCat = categories.find((c) => c.id === rel.categoryId);

                  return (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                      data-product-shop
                    >
                      <div className="w-full h-40 overflow-hidden">
                        <img
                          src={rel.imageUrl}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        {relCat && (
                          <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-primary-100 text-primary-700 font-medium mb-2 whitespace-nowrap">
                            {relCat.name}
                          </span>
                        )}
                        <h3 className="text-sm font-semibold text-foreground-900 font-heading line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {rel.title}
                        </h3>
                        <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
                          <span className="whitespace-nowrap">{rel.author}</span>
                          <span className="whitespace-nowrap">{rel.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}